import * as XLSX from "xlsx-js-style";
import JSZip from "jszip";

/**
 * "Analysis" sheet of the QA Done report: KPI cards, developer performance,
 * squad comparison, collaboration matrix, key insights and a task breakdown,
 * all computed from the sprint's Done tasks.
 *
 * Fair share: a task is worth 1 point split equally between its developers
 * (an unassigned task counts 1 for "Unassigned"). A developer's "Tasks
 * (Points)" is how many tasks they worked on; solo + shared always add up to it.
 */

const UNASSIGNED = "Unassigned";
const nameOf = (a) => a?.fullNameEn || a?.nickname || "Unknown";
const ratio = (a, b) => (b ? a / b : 0);
const percent = (a, b) => Math.round(ratio(a, b) * 100);
const typeLabel = (t) => (t === "feat" ? "Feature" : t === "fix" ? "Bug Fix" : t === "improvement" ? "Improvement" : "Other");

/** Most frequent value of `list` (first one on ties), or null. */
const mostCommon = (list) => {
  const counts = new Map();
  list.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
};

export const computeAnalysis = (tasks, squads = []) => {
  const squadName = (id) => squads.find((s) => s._id === id)?.name || "No squad";
  const rows = [...tasks]
    .sort((a, b) => (a.number || 0) - (b.number || 0))
    .map((t) => {
      const devs = [...new Set((t.assignees || []).map(nameOf))];
      return {
        title: t.title,
        squad: squadName(t.squad),
        type: typeLabel(t.taskType),
        devs,
        credit: 1 / Math.max(devs.length, 1),
        rejected: (t.qaRejectedCount || 0) > 0,
        late: Boolean(t.deadlineMissed),
      };
    });

  // ---- developers ----
  const names = [...new Set(rows.flatMap((r) => r.devs))];
  const devRows = [...names, UNASSIGNED]
    .map((dev) => {
      const mine = dev === UNASSIGNED ? rows.filter((r) => !r.devs.length) : rows.filter((r) => r.devs.includes(dev));
      const shared = dev === UNASSIGNED ? 0 : mine.filter((r) => r.devs.length > 1).length;
      const rejected = mine.filter((r) => r.rejected).length;
      const late = mine.filter((r) => r.late).length;
      return {
        dev,
        squad: mostCommon(mine.map((r) => r.squad)) || "-",
        tasks: mine.length,
        solo: mine.length - shared,
        shared,
        fair: mine.reduce((n, r) => n + r.credit, 0),
        rejected,
        late,
        net: mine.length - rejected - late,
      };
    })
    // "Unassigned" only appears when some task has no developer.
    .filter((d) => d.dev !== UNASSIGNED || d.tasks > 0);
  const named = devRows.filter((d) => d.dev !== UNASSIGNED).sort((a, b) => b.fair - a.fair || a.dev.localeCompare(b.dev));
  const unassigned = devRows.find((d) => d.dev === UNASSIGNED);
  named.forEach((d, i) => (d.rank = i + 1));
  const ordered = [...named, ...(unassigned ? [unassigned] : [])];
  const totalPoints = ordered.reduce((n, d) => n + d.tasks, 0);
  const totals = {
    tasks: totalPoints,
    solo: ordered.reduce((n, d) => n + d.solo, 0),
    shared: ordered.reduce((n, d) => n + d.shared, 0),
    fair: ordered.reduce((n, d) => n + d.fair, 0),
    rejected: ordered.reduce((n, d) => n + d.rejected, 0),
    late: ordered.reduce((n, d) => n + d.late, 0),
    net: ordered.reduce((n, d) => n + d.net, 0),
  };
  ordered.forEach((d) => {
    d.pct = ratio(d.tasks, totalPoints);
    d.match = d.solo + d.shared === d.tasks;
  });
  const allMatch = ordered.every((d) => d.match) && Math.abs(totals.fair - rows.length) < 1e-9;

  // ---- squads ----
  const squadNames = [...new Set(rows.map((r) => r.squad))];
  const bySquad = squadNames.map((name) => {
    const mine = rows.filter((r) => r.squad === name);
    const assigned = mine.filter((r) => r.devs.length);
    const devs = named.filter((d) => d.squad === name).length;
    const fairAssigned = assigned.reduce((n, r) => n + r.credit, 0);
    return {
      name,
      tasks: mine.length,
      devs,
      points: mine.reduce((n, r) => n + Math.max(r.devs.length, 1), 0),
      fairAssigned,
      avg: ratio(fairAssigned, devs),
      shared: mine.filter((r) => r.devs.length > 1).length,
      unassigned: mine.filter((r) => !r.devs.length).length,
      feature: mine.filter((r) => r.type === "Feature").length,
      bug: mine.filter((r) => r.type === "Bug Fix").length,
      other: mine.filter((r) => r.type === "Other").length,
    };
  });
  const squadTotal = (key) => bySquad.reduce((n, s) => n + s[key], 0);
  const squadSum = {
    tasks: rows.length,
    devs: named.length,
    points: squadTotal("points"),
    fairAssigned: squadTotal("fairAssigned"),
    shared: squadTotal("shared"),
    unassigned: squadTotal("unassigned"),
    feature: squadTotal("feature"),
    bug: squadTotal("bug"),
    other: squadTotal("other"),
  };
  squadSum.avg = ratio(squadSum.fairAssigned, squadSum.devs);

  // ---- collaboration ----
  const devNames = named.map((d) => d.dev);
  const matrix = devNames.map((a) =>
    devNames.map((b) => (a === b ? null : rows.filter((r) => r.devs.includes(a) && r.devs.includes(b)).length)),
  );
  let strongest = null;
  devNames.forEach((a, i) =>
    devNames.forEach((b, j) => {
      if (j > i && matrix[i][j] > (strongest?.n || 0)) strongest = { a, b, n: matrix[i][j] };
    }),
  );

  // ---- insights ----
  const insights = [];
  const sprintTasks = rows.length;
  if (named.length) {
    const top = named[0];
    const bigger = bySquad
      .filter((s) => s.fairAssigned < top.fair)
      .sort((a, b) => b.fairAssigned - a.fairAssigned)[0];
    insights.push(
      `Top contributor: ${top.dev} with ${top.fair.toFixed(1)} fair-share tasks — ${percent(top.fair, sprintTasks)}% of sprint work${
        bigger ? `, more than all of ${bigger.name}'s assigned work (${bigger.fairAssigned.toFixed(1)})` : ""
      }.`,
    );
    if (named.length > 1) {
      const low = named[named.length - 1];
      insights.push(`Lowest contributor: ${low.dev} with ${low.fair.toFixed(1)} fair-share task(s) — worth checking capacity or missing tracking.`);
    }
  }
  if (bySquad.length) {
    insights.push(
      `Workload balance: ${bySquad.map((s) => `${s.name} did ${s.tasks} tasks with ${s.devs} devs (${s.avg.toFixed(1)} per dev)`).join("; ")}.`,
    );
    insights.push(
      `Collaboration: ${bySquad.map((s) => `${percent(s.shared, s.tasks)}% of ${s.name} tasks are shared`).join(" vs ")}.${
        strongest ? ` Strongest pair: ${strongest.a} + ${strongest.b} (${strongest.n} shared tasks).` : ""
      }`,
    );
  }
  insights.push(
    squadSum.unassigned
      ? `Unassigned work: ${squadSum.unassigned} tasks (${percent(squadSum.unassigned, sprintTasks)}% of the sprint) have no developer — ${bySquad
          .map((s) => `${s.name}: ${s.unassigned}`)
          .join(", ")}.`
      : "Unassigned work: every task has at least one developer.",
  );
  if (sprintTasks) {
    insights.push(
      `Work mix: ${squadSum.feature} features, ${squadSum.bug} bug fixes, ${squadSum.other} other.${
        bySquad.length > 1
          ? ` Bug fixes are ${bySquad.map((s, i) => `${percent(s.bug, s.tasks)}% of ${s.name}'s work`).join(" vs ")}.`
          : ""
      }`,
    );
  }
  insights.push(
    totals.rejected || totals.late
      ? `Quality: ${totals.rejected} task(s) rejected by QA and ${totals.late} finished after their due date.`
      : "Quality: no QA rejections or late tasks recorded — confirm the QA Rejected / Late columns were actually filled in.",
  );

  return {
    taskCount: rows.length,
    kpis: {
      tasks: rows.length,
      points: totalPoints,
      shared: rows.filter((r) => r.devs.length > 1).length,
      unassigned: rows.filter((r) => !r.devs.length).length,
      rejected: rows.filter((r) => r.rejected).length,
      late: rows.filter((r) => r.late).length,
    },
    devRows: ordered,
    totals,
    allMatch,
    bySquad,
    squadSum,
    devNames,
    matrix,
    insights,
    breakdown: rows,
  };
};

// ---------------------------------------------------------------------------
// Sheet (orange theme)
// ---------------------------------------------------------------------------

const C = {
  deep: "7C2D12",
  label: "9A3412",
  head: "FED7AA",
  soft: "FFF7ED",
  total: "FFEDD5",
  insight: "FFF1E0",
  text: "1F1F23",
  muted: "6B6B73",
  ok: "DCFCE7",
  okText: "166534",
  bad: "FEE2E2",
  badText: "B91C1C",
  bug: "FEF08A",
  bugText: "854D0E",
  line: "E5C9A8",
};
const HEAT = [null, "FFEDD5", "FED7AA", "FDBA74", "FB923C"];
const edge = { style: "thin", color: { rgb: C.line } };
const box = { top: edge, bottom: edge, left: edge, right: edge };
const font = (extra = {}) => ({ name: "Calibri", sz: 10, color: { rgb: C.text }, ...extra });
const fill = (rgb) => (rgb ? { patternType: "solid", fgColor: { rgb } } : undefined);
const style = ({ bg, bold, color, sz, align = "center", wrap = false, fmt, border = true, italic } = {}) => ({
  font: font({ bold, italic, ...(color ? { color: { rgb: color } } : {}), ...(sz ? { sz } : {}) }),
  fill: fill(bg),
  alignment: { horizontal: align, vertical: "center", wrapText: wrap },
  ...(border ? { border: box } : {}),
  ...(fmt ? { numFmt: fmt } : {}),
});

/** Builds the sheet and the cell ranges the charts / conditional formats need (1-based rows). */
export const buildAnalysis = (label, analysis) => {
  const a = analysis;
  const layout = { devs: a.devNames.length, squads: a.bySquad.length };
  const ws = {};
  const merges = [];
  const heights = [];
  const LAST = 12; // column M
  const put = (r, c, v, s) => {
    ws[XLSX.utils.encode_cell({ r, c })] = { v, t: typeof v === "number" ? "n" : "s", s };
  };
  const span = (r, c1, c2, v, s) => {
    for (let c = c1; c <= c2; c += 1) put(r, c, c === c1 ? v : "", s);
    if (c2 > c1) merges.push({ s: { r, c: c1 }, e: { r, c: c2 } });
  };
  let r = 1;

  // Title
  span(r, 1, LAST, `${label.toUpperCase()} — QA ANALYSIS DASHBOARD`, style({ bg: C.deep, bold: true, color: "FFFFFF", sz: 16, align: "left", border: false }));
  heights[r] = 30;
  r += 1;
  span(r, 1, LAST, "Figures are computed from the sprint's Done tasks when the report is downloaded (a task is worth 1 point split equally between its developers).", style({ italic: true, color: C.muted, sz: 9, align: "left", border: false }));
  r += 2;

  // KPI cards
  [
    ["Total Tasks", a.kpis.tasks],
    ["Total Points", a.kpis.points],
    ["Shared Tasks", a.kpis.shared],
    ["Unassigned Tasks", a.kpis.unassigned],
    ["QA Rejections", a.kpis.rejected],
    ["Late Tasks", a.kpis.late],
  ].forEach(([title, value], i) => {
    const c = 1 + i * 2;
    span(r, c, c + 1, title, style({ bg: C.label, bold: true, color: "FFFFFF", sz: 9 }));
    span(r + 1, c, c + 1, value || 0, style({ bg: C.soft, bold: true, color: C.deep, sz: 20, fmt: "0" }));
  });
  heights[r + 1] = 38;
  r += 3;

  const section = (text) => {
    span(r, 1, LAST, text, style({ bg: C.deep, bold: true, color: "FFFFFF", align: "left", border: false }));
    heights[r] = 20;
    r += 1;
  };
  const header = (cols, start = 1) => {
    cols.forEach((t, i) => put(r, start + i, t, style({ bg: C.head, bold: true, color: C.deep, sz: 9, wrap: true })));
    heights[r] = 24;
    r += 1;
  };

  // Developer performance
  section("DEVELOPER PERFORMANCE");
  header(["Developer", "Squad", "Tasks (Points)", "Solo Tasks", "Shared Tasks", "Fair Share", "% of Points", "QA Rejected", "Late", "Net Score", "Rank (Fair Share)", "Summary Check"]);
  layout.devFirst = r + 1;
  layout.devLast = r + a.devNames.length; // developers only (Unassigned is not charted)
  layout.devAllLast = r + a.devRows.length;
  a.devRows.forEach((d, i) => {
    const zebra = i % 2 ? undefined : C.soft;
    const dash = (n) => (n ? n : "-");
    put(r, 1, d.dev, style({ bg: zebra, align: "left", bold: d.dev !== UNASSIGNED }));
    put(r, 2, d.squad, style({ bg: zebra }));
    put(r, 3, d.tasks, style({ bg: zebra, fmt: "0" }));
    put(r, 4, d.solo, style({ bg: zebra, fmt: "0" }));
    put(r, 5, d.shared ? d.shared : "-", style({ bg: zebra, fmt: "0" }));
    put(r, 6, d.fair, style({ bg: zebra, fmt: "0.0", bold: true }));
    put(r, 7, d.pct, style({ bg: zebra, fmt: "0.0%" }));
    put(r, 8, dash(d.rejected), style({ bg: d.rejected ? C.bad : zebra, color: d.rejected ? C.badText : undefined }));
    put(r, 9, dash(d.late), style({ bg: d.late ? C.bad : zebra, color: d.late ? C.badText : undefined }));
    put(r, 10, d.net, style({ bg: zebra, fmt: "0", bold: true }));
    put(r, 11, d.rank ?? "—", style({ bg: zebra }));
    put(r, 12, d.match ? "✓ Match" : "✗ Check", style({ bg: d.match ? C.ok : C.bad, color: d.match ? C.okText : C.badText, bold: true }));
    r += 1;
  });
  const t = a.totals;
  const totalStyle = (extra = {}) => style({ bg: C.total, bold: true, ...extra });
  put(r, 1, "TOTAL", totalStyle({ align: "left" }));
  put(r, 2, "", totalStyle());
  put(r, 3, t.tasks, totalStyle({ fmt: "0" }));
  put(r, 4, t.solo, totalStyle({ fmt: "0" }));
  put(r, 5, t.shared, totalStyle({ fmt: "0" }));
  put(r, 6, t.fair, totalStyle({ fmt: "0.0" }));
  put(r, 7, t.tasks ? 1 : 0, totalStyle({ fmt: "0.0%" }));
  put(r, 8, t.rejected || "-", totalStyle());
  put(r, 9, t.late || "-", totalStyle());
  put(r, 10, t.net, totalStyle({ fmt: "0" }));
  put(r, 11, "", totalStyle());
  put(r, 12, a.allMatch ? "✓ All match" : "✗ Check", style({ bg: a.allMatch ? C.ok : C.bad, color: a.allMatch ? C.okText : C.badText, bold: true }));
  r += 2;

  // Squad comparison
  section("SQUAD COMPARISON");
  header(["Metric", ...a.bySquad.map((s) => s.name), "Total"]);
  layout.sqHeader = r; // header row (1-based)
  const squadLines = [
    ["Tasks Completed", "tasks", "0"],
    ["Developers", "devs", "0"],
    ["Points (1 per developer per task)", "points", "0"],
    ["Assigned Work (Fair Share)", "fairAssigned", "0.0"],
    ["Avg Tasks per Developer", "avg", "0.0"],
    ["Shared Tasks", "shared", "0"],
    ["% Shared", null, "0.0%"],
    ["Unassigned Tasks", "unassigned", "0"],
    ["Feature", "feature", "0"],
    ["Bug Fix", "bug", "0"],
    ["Other", "other", "0"],
    ["% Bug Fix", null, "0.0%"],
  ];
  squadLines.forEach(([title, key, fmt], i) => {
    const zebra = i % 2 ? undefined : C.soft;
    put(r, 1, title, style({ bg: zebra, align: "left" }));
    const cellValue = (s) =>
      key ? s[key] : title === "% Shared" ? ratio(s.shared, s.tasks) : ratio(s.bug, s.tasks);
    [...a.bySquad, a.squadSum].forEach((s, j) => {
      const v = cellValue(s);
      // Numeric zeros (shown as "-") so the charts read them as 0.
      put(r, 2 + j, v, style({ bg: j === a.bySquad.length ? C.total : zebra, bold: j === a.bySquad.length, fmt: `${fmt};-${fmt};"-"` }));
    });
    r += 1;
  });
  r += 1;

  // Collaboration matrix
  section("COLLABORATION MATRIX — shared tasks between developers");
  if (a.devNames.length) {
    header(["Developer", ...a.devNames]);
    layout.matrixFirst = r + 1;
    layout.matrixLast = r + a.devNames.length;
    a.devNames.forEach((name, i) => {
      put(r, 1, name, style({ bg: C.soft, bold: true, align: "left" }));
      a.matrix[i].forEach((n, j) => {
        if (n === null) put(r, 2 + j, "—", style({ color: C.muted }));
        else put(r, 2 + j, n || "-", style({ bg: HEAT[Math.min(n, 4)] || undefined, bold: n > 0, color: n ? C.deep : C.muted }));
      });
      r += 1;
    });
  } else {
    span(r, 1, LAST, "No developers on the Done tasks.", style({ italic: true, color: C.muted, align: "left", border: false }));
    r += 1;
  }
  r += 1;

  // Key insights
  section("KEY INSIGHTS");
  a.insights.forEach((text) => {
    span(r, 1, LAST, `▸ ${text}`, style({ bg: C.insight, align: "left", wrap: true, border: false }));
    heights[r] = text.length > 150 ? 30 : 20;
    r += 1;
  });
  r += 1;

  // Task breakdown
  section("TASK BREAKDOWN");
  header(["Task", "Squad", "Type", "Devs on Task", "Credit / Dev", "QA Rejected", "Late"]);
  put(r - 1, 8, "Developer(s)", style({ bg: C.head, bold: true, color: C.deep, sz: 9 }));
  for (let c = 9; c <= LAST; c += 1) put(r - 1, c, "", style({ bg: C.head }));
  merges.push({ s: { r: r - 1, c: 8 }, e: { r: r - 1, c: LAST } });
  a.breakdown.forEach((row) => {
    const zebra = undefined;
    put(r, 1, row.title, style({ bg: zebra, align: "left", wrap: true }));
    put(r, 2, row.squad, style({ bg: zebra }));
    put(r, 3, row.type, style({
      bg: row.type === "Feature" ? C.total : row.type === "Bug Fix" ? C.bug : zebra,
      color: row.type === "Feature" ? C.label : row.type === "Bug Fix" ? C.bugText : undefined,
    }));
    put(r, 4, row.devs.length, style({ bg: row.devs.length > 1 ? C.bad : zebra, color: row.devs.length > 1 ? C.badText : undefined, fmt: "0" }));
    put(r, 5, row.credit, style({ bg: zebra, fmt: "0.00" }));
    put(r, 6, row.rejected ? "Yes" : "No", style({ bg: row.rejected ? C.bad : zebra, color: row.rejected ? C.badText : undefined }));
    put(r, 7, row.late ? "Yes" : "No", style({ bg: row.late ? C.bad : zebra, color: row.late ? C.badText : undefined }));
    span(r, 8, LAST, row.devs.length ? row.devs.join(", ") : UNASSIGNED, style({ bg: zebra, align: "left", wrap: true }));
    heights[r] = Math.max(1, Math.ceil(row.title.length / 34)) * 14 + 4;
    r += 1;
  });

  const widths = [2, 32, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 18];
  // Collaboration matrix may use more columns than the dashboard.
  for (let c = widths.length; c < a.devNames.length + 2; c += 1) widths.push(13);
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: r, c: Math.max(widths.length - 1, LAST) } });
  ws["!cols"] = widths.map((wch) => ({ wch }));
  ws["!rows"] = Array.from({ length: r + 1 }, (_, i) => (heights[i] ? { hpt: heights[i] } : {}));
  ws["!merges"] = merges;
  ws["!sheetViews"] = [{ showGridLines: false }];
  return { ws, layout };
};

export const buildAnalysisSheet = (label, analysis) => buildAnalysis(label, analysis).ws;

// ---------------------------------------------------------------------------
// Charts + conditional formats (added to the file after SheetJS writes it —
// SheetJS cannot write them; same approach as the attendance report).
// ---------------------------------------------------------------------------

const col = (i) => XLSX.utils.encode_col(i);
const esc = (t) => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const TXT = (sz) => `<c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="${sz}"/></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr>`;
const chartTitle = (text) =>
  `<c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1100" b="1"/></a:pPr><a:r><a:rPr lang="en-US" sz="1100" b="1"/><a:t>${esc(text)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title>`;
const fillXml = (rgb) => `<c:spPr><a:solidFill><a:srgbClr val="${rgb}"/></a:solidFill></c:spPr>`;
const catAx = (id, cross, pos, reverse) =>
  `<c:catAx><c:axId val="${id}"/><c:scaling><c:orientation val="${reverse ? "maxMin" : "minMax"}"/></c:scaling><c:delete val="0"/><c:axPos val="${pos}"/><c:numFmt formatCode="General" sourceLinked="0"/><c:tickLblPos val="nextTo"/><c:spPr><a:ln><a:solidFill><a:srgbClr val="BFBFBF"/></a:solidFill></a:ln></c:spPr>${TXT(900)}<c:crossAx val="${cross}"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/></c:catAx>`;
const valAx = (id, cross, pos) =>
  `<c:valAx><c:axId val="${id}"/><c:scaling><c:orientation val="minMax"/><c:min val="0"/></c:scaling><c:delete val="0"/><c:axPos val="${pos}"/><c:majorGridlines><c:spPr><a:ln><a:solidFill><a:srgbClr val="E7E6E6"/></a:solidFill></a:ln></c:spPr></c:majorGridlines><c:numFmt formatCode="0" sourceLinked="0"/><c:tickLblPos val="nextTo"/>${TXT(900)}<c:crossAx val="${cross}"/><c:crosses val="max"/><c:crossBetween val="between"/></c:valAx>`;
const dLbls = `<c:dLbls><c:numFmt formatCode="General;-General;;" sourceLinked="0"/><c:spPr><a:noFill/></c:spPr>${TXT(800)}<c:showLegendKey val="0"/><c:showVal val="1"/><c:showCatName val="0"/><c:showSerName val="0"/><c:showPercent val="0"/><c:showBubbleSize val="0"/></c:dLbls>`;
const chartSpace = (title, plot) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:roundedCorners val="0"/>
  <c:chart>${chartTitle(title)}<c:autoTitleDeleted val="0"/>
    <c:plotArea><c:layout/>${plot}</c:plotArea>
    <c:legend><c:legendPos val="b"/><c:overlay val="0"/>${TXT(900)}</c:legend>
    <c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/>
  </c:chart>
  <c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill><a:ln><a:solidFill><a:srgbClr val="D9D9D9"/></a:solidFill></a:ln></c:spPr>
</c:chartSpace>`;
const ser = (idx, name, cat, val, color, labels) =>
  `<c:ser><c:idx val="${idx}"/><c:order val="${idx}"/><c:tx>${name}</c:tx>${fillXml(color)}<c:invertIfNegative val="0"/>${labels ? dLbls : ""}<c:cat><c:strRef><c:f>${cat}</c:f></c:strRef></c:cat><c:val><c:numRef><c:f>${val}</c:f></c:numRef></c:val></c:ser>`;
const literal = (t) => `<c:v>${esc(t)}</c:v>`;
const ref = (f) => `<c:strRef><c:f>${f}</c:f></c:strRef>`;

/** Developer workload: tasks (points) vs fair share, one bar pair per developer. */
const workloadChart = (l) => {
  const cat = `Analysis!$B$${l.devFirst}:$B$${l.devLast}`;
  return chartSpace(
    "Developer Workload — Points vs Fair Share",
    `<c:barChart><c:barDir val="bar"/><c:grouping val="clustered"/><c:varyColors val="0"/>` +
      ser(0, literal("Tasks (Points)"), cat, `Analysis!$D$${l.devFirst}:$D$${l.devLast}`, "FDBA74", true) +
      ser(1, literal("Fair Share"), cat, `Analysis!$G$${l.devFirst}:$G$${l.devLast}`, "9A3412", true) +
      `<c:gapWidth val="60"/><c:axId val="610100"/><c:axId val="610200"/></c:barChart>` +
      catAx(610100, 610200, "l", true) +
      valAx(610200, 610100, "b"),
  );
};

/** Work type by squad: stacked Feature / Bug Fix / Other columns per squad. */
const workTypeChart = (l) => {
  const first = col(2);
  const last = col(1 + l.squads);
  const cat = `Analysis!$${first}$${l.sqHeader}:$${last}$${l.sqHeader}`;
  const row = (n) => `Analysis!$${first}$${l.sqHeader + n}:$${last}$${l.sqHeader + n}`;
  const name = (n) => ref(`Analysis!$B$${l.sqHeader + n}`);
  return chartSpace(
    "Work Type by Squad",
    `<c:barChart><c:barDir val="col"/><c:grouping val="stacked"/><c:varyColors val="0"/>` +
      ser(0, name(9), cat, row(9), "9A3412", true) +
      ser(1, name(10), cat, row(10), "FBBF24", true) +
      ser(2, name(11), cat, row(11), "A8A29E", true) +
      `<c:gapWidth val="80"/><c:overlap val="100"/><c:axId val="620100"/><c:axId val="620200"/></c:barChart>` +
      catAx(620100, 620200, "b", false) +
      valAx(620200, 620100, "l").replace('<c:crosses val="max"/>', '<c:crosses val="autoZero"/>'),
  );
};

const anchor = (id, name, from, to, rid) => `
  <xdr:twoCellAnchor editAs="oneCell">
    <xdr:from><xdr:col>${from.col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${from.row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
    <xdr:to><xdr:col>${to.col}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${to.row}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
    <xdr:graphicFrame macro="">
      <xdr:nvGraphicFramePr><xdr:cNvPr id="${id}" name="${esc(name)}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr>
      <xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>
      <a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="${rid}"/></a:graphicData></a:graphic>
    </xdr:graphicFrame>
    <xdr:clientData/>
  </xdr:twoCellAnchor>`;

const conditionalFormats = (l) => {
  let out = "";
  let priority = 1;
  if (l.devs) {
    out += `<conditionalFormatting sqref="G${l.devFirst}:G${l.devAllLast}"><cfRule type="dataBar" priority="${priority++}"><dataBar><cfvo type="min"/><cfvo type="max"/><color rgb="FFFB923C"/></dataBar></cfRule></conditionalFormatting>`;
    out += `<conditionalFormatting sqref="C${l.matrixFirst}:${col(1 + l.devs)}${l.matrixLast}"><cfRule type="colorScale" priority="${priority++}"><colorScale><cfvo type="num" val="0"/><cfvo type="max"/><color rgb="FFFFF7ED"/><color rgb="FFFB923C"/></colorScale></cfRule></conditionalFormatting>`;
  }
  return out;
};

const REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const REL_TYPE = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const rels = (items) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="${REL_NS}">${items}</Relationships>`;

/**
 * Add the two dashboard charts and the data bars / heat map to the Analysis
 * sheet of an already written workbook. `sheetNumber` is the sheet's position
 * (1-based) in the workbook, i.e. the n of xl/worksheets/sheet<n>.xml.
 */
export const enhanceAnalysisWorkbook = async (buffer, layout, sheetNumber) => {
  const zip = await JSZip.loadAsync(buffer);
  const sheetPath = `xl/worksheets/sheet${sheetNumber}.xml`;
  let sheet = await zip.file(sheetPath).async("string");

  const cf = conditionalFormats(layout);
  if (cf) sheet = sheet.includes("</mergeCells>") ? sheet.replace("</mergeCells>", `</mergeCells>${cf}`) : sheet.replace("</sheetData>", `</sheetData>${cf}`);

  const charts = [];
  const anchors = [];
  if (layout.devs) {
    charts.push(workloadChart(layout));
    // Right of the dashboard, level with the KPI cards and developer table.
    anchors.push(anchor(2, "Developer workload", { col: 14, row: 3 }, { col: 23, row: 24 }, "rId1"));
  }
  if (layout.squads) {
    charts.push(workTypeChart(layout));
    // Beside the squad comparison table (which spans B..Total).
    const from = 3 + layout.squads + 1;
    anchors.push(
      anchor(3, "Work type by squad", { col: from, row: layout.sqHeader - 1 }, { col: Math.max(from + 6, 13), row: layout.sqHeader + 13 }, `rId${charts.length}`),
    );
  }

  if (charts.length) {
    if (!/xmlns:r=/.test(sheet.slice(0, 800))) {
      sheet = sheet.replace("<worksheet ", `<worksheet xmlns:r="${REL_TYPE}" `);
    }
    const drawingTag = '<drawing r:id="rIdAnalysisDrawing"/>';
    sheet = /<(legacyDrawing|tableParts|extLst)\b/.test(sheet)
      ? sheet.replace(/<(legacyDrawing|tableParts|extLst)\b/, `${drawingTag}<$1`)
      : sheet.replace("</worksheet>", `${drawingTag}</worksheet>`);

    const relsPath = `xl/worksheets/_rels/sheet${sheetNumber}.xml.rels`;
    const rel = `<Relationship Id="rIdAnalysisDrawing" Type="${REL_TYPE}/drawing" Target="../drawings/drawing1.xml"/>`;
    const existing = zip.file(relsPath) ? await zip.file(relsPath).async("string") : null;
    zip.file(relsPath, existing ? existing.replace("</Relationships>", `${rel}</Relationships>`) : rels(rel));

    zip.file(
      "xl/drawings/drawing1.xml",
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">${anchors.join("")}</xdr:wsDr>`,
    );
    zip.file(
      "xl/drawings/_rels/drawing1.xml.rels",
      rels(charts.map((_, i) => `<Relationship Id="rId${i + 1}" Type="${REL_TYPE}/chart" Target="../charts/chart${i + 1}.xml"/>`).join("")),
    );
    charts.forEach((xml, i) => zip.file(`xl/charts/chart${i + 1}.xml`, xml));

    let types = await zip.file("[Content_Types].xml").async("string");
    types = types.replace(
      "</Types>",
      '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>' +
        charts
          .map((_, i) => `<Override PartName="/xl/charts/chart${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`)
          .join("") +
        "</Types>",
    );
    zip.file("[Content_Types].xml", types);
  }
  zip.file(sheetPath, sheet);
  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
};
